import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiClient } from '@safermark/shared/api';
import type { User } from '@safermark/shared/types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (password: string, email: string) => Promise<any>;
  logout: () => Promise<void>;
  refreshUser: (force?: boolean) => Promise<User>;
  refreshUserAfterPayment: () => Promise<User>;
  refreshPremiumStatus: () => Promise<User>;
  isAuthenticated: boolean;
  isPremium: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<number>(0);
  const [lastPremiumRefresh, setLastPremiumRefresh] = useState<number>(0);

  // Set up API client callbacks for token refresh and logout
  useEffect(() => {
    const handleTokenRefresh = (newToken: string, newRefreshToken: string) => {
      console.log('Token refreshed automatically');
      setToken(newToken);
      
      // Update localStorage
      localStorage.setItem('auth_token', newToken);
      localStorage.setItem('refresh_token', newRefreshToken);
      
      // Update API client token
      apiClient.setToken(newToken);
    };

    const handleLogout = () => {
      console.log('Automatic logout due to token refresh failure');
      setUser(null);
      setToken(null);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('refresh_token');
      apiClient.setTokens(null, null);
    };

    apiClient.setCallbacks(handleTokenRefresh, handleLogout);
  }, []);

  useEffect(() => {
    // Check for stored auth on mount
    const storedToken = localStorage.getItem('auth_token');
    const storedRefreshToken = localStorage.getItem('refresh_token');
    const storedUser = localStorage.getItem('auth_user');
    
    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        
        // Set tokens in API client
        apiClient.setTokens(storedToken, storedRefreshToken);
      } catch (error) {
        // Clear invalid stored data
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('refresh_token');
      }
    }
    
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    const data = await apiClient.login({ email, password });
    setUser(data.user);
    setToken(data.token);
    
    // Store auth data
    localStorage.setItem('auth_token', data.token);
    localStorage.setItem('auth_user', JSON.stringify(data.user));
    localStorage.setItem('refresh_token', data.refreshToken);
    
    // Set tokens in API client for automatic refresh
    apiClient.setTokens(data.token, data.refreshToken);
  };

  const register = async (password: string, email: string) => {
    const data = await apiClient.register({ email, password });
    
    // Registration no longer returns tokens - user must verify email first
    // Return the registration response so the UI can show appropriate messages
    return data;
  };

  const logout = async () => {
    const storedRefreshToken = localStorage.getItem('refresh_token');
    
    // Attempt to logout on server if we have a refresh token
    if (storedRefreshToken) {
      try {
        await apiClient.logout(storedRefreshToken);
      } catch (error) {
        console.warn('Server logout failed, continuing with local logout:', error);
      }
    }
    
    // Clear local state and storage
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('refresh_token');
    
    // Clear API client tokens
    apiClient.setTokens(null, null);
  };

  const refreshUser = useCallback(async (force: boolean = false) => {
    if (!token) {
      throw new Error('No token available');
    }

    const now = Date.now();
    const minInterval = 15000; // 15 seconds (reduced for more responsive updates)
    const maxCacheAge = 10 * 60 * 1000; // 10 minutes (reduced for more frequent refreshes)
    
    // Force refresh if cache is older than 10 minutes
    const cacheAge = now - lastRefresh;
    const cacheExpired = cacheAge > maxCacheAge;
    
    if (cacheExpired) {
      console.log('refreshUser: Cache expired (> 10 minutes), forcing refresh');
      force = true;
    }
    
    // Rate limiting: only refresh if it's been more than 15 seconds since last refresh
    // or if explicitly forced or cache expired
    if (!force && (now - lastRefresh) < minInterval) {
      console.log('refreshUser: Skipping refresh, too recent (< 15s)');
      return user!; // Return cached user data
    }

    try {
      const data = await apiClient.getUserProfile();
      console.log('Fresh user data from API:', {
        membershipType: data.user.membershipType,
        isPremium: data.user.isPremium,
        subscriptionId: data.user.subscriptionId || 'none',
        prepaidUntil: data.user.prepaidUntil,
        subscriptionCurrentPeriodEnd: data.user.subscriptionCurrentPeriodEnd,
        // Enhanced subscription information
        subscriptionPlanId: data.user.subscriptionPlanId,
        subscriptionStatus: data.user.subscriptionStatus,
        subscriptionNextRenewal: data.user.subscriptionNextRenewal,
        subscriptionTrialEnd: data.user.subscriptionTrialEnd,
        userId: data.user.id,
        emailVerified: data.user.emailVerified
      });
      
      setUser(data.user);
      setLastRefresh(now);
      
      // Update stored user data
      localStorage.setItem('auth_user', JSON.stringify(data.user));
      
      // Return the fresh user data for immediate checking
      return data.user;
    } catch (error: any) {
      // The API client will automatically handle 401 errors and token refresh
      // If we get here, it means the error is not recoverable
      if (error.message?.includes('Session expired')) {
        // Automatic logout was already handled by the API client callback
        throw new Error('Session expired - please login again');
      }
      throw new Error('Failed to refresh user data');
    }
  }, [token, user, lastRefresh]);

  // Special refresh function for after payment operations
  const refreshUserAfterPayment = useCallback(async () => {
    console.log('Refreshing user data after payment operation...');
    // Force refresh and reset the last refresh timestamp to ensure fresh data
    setLastRefresh(0);
    return await refreshUser(true);
  }, [refreshUser]);

  // Special refresh function for premium status checks with debouncing
  const refreshPremiumStatus = useCallback(async () => {
    if (!token || !user) {
      return user!;
    }

    const now = Date.now();
    const minInterval = 30000; // 30 seconds debouncing
    
    // Rate limiting: only refresh if it's been more than 30 seconds since last premium refresh
    if ((now - lastPremiumRefresh) < minInterval) {
      console.log('refreshPremiumStatus: Skipping refresh, too recent (< 30s since last premium check)');
      return user!; // Return cached user data
    }

    console.log('refreshPremiumStatus: Refreshing premium status due to suspected mismatch');
    
    try {
      const data = await apiClient.getUserProfile();
      console.log('Premium status refresh - Fresh user data from API:', {
        membershipType: data.user.membershipType,
        isPremium: data.user.isPremium,
        subscriptionId: data.user.subscriptionId || 'none',
        prepaidUntil: data.user.prepaidUntil,
        subscriptionCurrentPeriodEnd: data.user.subscriptionCurrentPeriodEnd,
        // Enhanced subscription information
        subscriptionPlanId: data.user.subscriptionPlanId,
        subscriptionStatus: data.user.subscriptionStatus,
        subscriptionNextRenewal: data.user.subscriptionNextRenewal,
        subscriptionTrialEnd: data.user.subscriptionTrialEnd,
        userId: data.user.id,
        emailVerified: data.user.emailVerified
      });
      
      setUser(data.user);
      setLastPremiumRefresh(now);
      setLastRefresh(now); // Also update general refresh timestamp
      
      // Update stored user data
      localStorage.setItem('auth_user', JSON.stringify(data.user));
      
      // Return the fresh user data for immediate checking
      return data.user;
    } catch (error: any) {
      console.warn('Failed to refresh premium status:', error);
      // Don't throw error - just return existing user data to avoid disrupting UI
      return user!;
    }
  }, [token, user, lastPremiumRefresh]);

  const value: AuthContextType = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    refreshUserAfterPayment,
    refreshPremiumStatus,
    isAuthenticated: !!user && !!token,
    isPremium: user?.isPremium || false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}